import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { useAsync } from 'react-use';

interface OrgAccountsResponse {
  items: unknown[];
  count: number;
  scannedAt: string;
  message?: string;
}

/** Marker name for the 403 case (a subclassed Error breaks `instanceof` under
 * the test transpile target, so we key on `error.name` instead). */
const FORBIDDEN = 'ForbiddenError';

const useStyles = makeStyles(theme => ({
  json: {
    margin: 0,
    padding: theme.spacing(2),
    // Ctrl-F searchable: real text in a scrollable block, `pre` (no wrap) so the
    // browser's native find works across the whole document.
    whiteSpace: 'pre',
    overflow: 'auto',
    maxHeight: '70vh',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
}));

/**
 * D2 — the Admin pane's Org-Accounts viewer (feature 0010). Dumps the raw
 * `Org-Accounts-ListFunc` DynamoDB table content (via the admin backend) as
 * pretty-printed, Ctrl-F-searchable JSON.
 *
 * Access control is enforced server-side (the backend authorizes
 * adminOrgAccountsReadPermission before reading); the route is additionally
 * `RequirePermission`-gated as UX. A 403 (e.g. the route guard is bypassed) is
 * surfaced as a friendly message rather than a raw error.
 */
export const OrgAccountsAdminPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async (): Promise<
    OrgAccountsResponse
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('admin');
    const res = await fetchApi.fetch(`${baseUrl}/org-accounts`);
    if (res.status === 403) {
      throw Object.assign(new Error('You are not allowed to view this data.'), {
        name: FORBIDDEN,
      });
    }
    if (!res.ok) {
      throw new Error(`Request failed (${res.status})`);
    }
    return res.json();
  }, []);

  const renderBody = () => {
    if (loading) return <Progress />;
    if (error?.name === FORBIDDEN) {
      return (
        <WarningPanel
          severity="info"
          title="Admin access required"
          message="Only Backstage admins can view the raw Org-Accounts table content."
        />
      );
    }
    if (error) {
      return (
        <WarningPanel
          title="Failed to load Org-Accounts table"
          message={error.message}
        />
      );
    }
    const count = value?.count ?? 0;
    const title = value?.message
      ? value.message
      : `${count} account item(s) · scanned ${value?.scannedAt ?? ''}`;
    return (
      <InfoCard title={title}>
        <pre className={classes.json} data-testid="org-accounts-json">
          {JSON.stringify(value?.items ?? [], null, 2)}
        </pre>
      </InfoCard>
    );
  };

  return (
    <Page themeId="tool">
      <Header
        title="Org Accounts"
        subtitle="Raw Org-Accounts DynamoDB table content (admin only)"
      />
      <Content>{renderBody()}</Content>
    </Page>
  );
};
